import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Signal,
  ViewEncapsulation,
  WritableSignal,
  computed,
  effect,
  signal
} from "@angular/core";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { ILocation, IViewPair } from "src/app/interfaces/interfaces";
import { ExtenderService } from "src/app/services/extender.service";
import { FeatureAppService } from "src/app/services/feature-app.service";
import { FormService } from "src/app/services/form.service";
import { PagingService } from "src/app/services/paging.service";
import { DealerResponse, WorkshopSystemEnum } from "src/domain/client";
import { PageBaseComponent } from "../../base/page-base/page-base.component";
import { ReactiveFormsModule } from "@angular/forms";
import { PageContainerComponent } from "../../layout/page-container/page-container.component";
import { ButtonComponent } from "../../inputs/button/button.component";
import { LoadingComponent } from "../../layout/loading/loading.component";
import { RequestFailedComponent } from "../../layout/request-failed/request-failed.component";
import { MapComponent } from "../../layout/map/map.component";
import * as BookingState from "src/app/state";
import {
  MatButtonToggleGroup,
  MatButtonToggle
} from "@angular/material/button-toggle";
import { MatRadioGroup } from "@angular/material/radio";
import { CheckboxCardComponent } from "../../layout/checkbox-card/checkbox-card.component";
import { WorkshopInfoComponent } from "../../layout/workshop-info/workshop-info.component";
import { ViewMoreComponent } from "../../layout/view-more/view-more.component";
import {
  AppError,
  loadDealers,
  selectDealer,
  selectDealers,
  selectError,
  selectIsLoading,
  selectSelectedDealer,
  selectSelectedWorkshopSystems
} from "src/app/state";
import { Store } from "@ngrx/store";
import { toSignal } from "@angular/core/rxjs-interop";

type SortingType = "distance" | "time";

@Component({
  selector: "app-find-dealer",
  templateUrl: "./find-dealer.component.html",
  encapsulation: ViewEncapsulation.None,
  imports: [
    PageContainerComponent,
    ButtonComponent,
    LoadingComponent,
    RequestFailedComponent,
    MapComponent,
    ReactiveFormsModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatRadioGroup,
    CheckboxCardComponent,
    WorkshopInfoComponent,
    ViewMoreComponent,
    TranslateModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FindDealerComponent
  extends PageBaseComponent
  implements OnInit, OnDestroy
{
  readonly listSortLocation = signal<ILocation | null>(null);
  readonly clientLocation = signal<ILocation | null>(null);
  readonly sortLocation = signal<ILocation | null>(null);
  readonly hasSearched = signal(false);

  readonly selectedWorkshopSystems: Signal<WorkshopSystemEnum[] | null>;
  readonly sortedDealers: Signal<DealerResponse[] | null>;
  readonly selectedDealer: Signal<DealerResponse | null>;
  readonly dealers: Signal<DealerResponse[] | null>;
  readonly dealerLocations: Signal<ILocation[]>;
  readonly mapCenter: Signal<ILocation | null>;
  readonly error: Signal<AppError | null>;
  readonly isLoading: Signal<boolean>;

  public sortingPreference: WritableSignal<SortingType> = signal("time");

  public autoSortEnabled = true;
  public sortingData: IViewPair[];

  public sortingSubscription: Subscription = null;
  public workshopSubscription: Subscription = null;

  constructor(
    public formService: FormService,
    private translateService: TranslateService,
    public extenderService: ExtenderService,
    public pagingService: PagingService,
    private featureAppService: FeatureAppService,
    private store: Store
  ) {
    super();
    this.formService.findDealerForm.markAsPristine();
    this.formService.previousFormValue = this.formService.findDealerForm.value;

    this.isLoading = this.store.selectSignal(selectIsLoading);
    this.error = this.store.selectSignal(selectError);
    this.selectedDealer = this.store.selectSignal(selectSelectedDealer);
    const unsortedDealers = this.store.selectSignal(selectDealers);
    this.selectedWorkshopSystems = this.store.selectSignal(
      selectSelectedWorkshopSystems
    );

    this.sortedDealers = computed(() => {
      const dealers = unsortedDealers();
      if (!dealers) return null;

      const preference = this.sortingPreference();
      const location = this.listSortLocation() || this.clientLocation();
      const selection = this.selectedWorkshopSystems();

      if (preference === "distance" && location) {
        return this._sortDealersByDistance(dealers, location);
      } else if (preference === "time" && selection) {
        return this._sortDealersByTime(dealers, selection);
      }

      return dealers;
    });

    this.mapCenter = computed(() => {
      const searched = this.sortLocation();
      if (searched) return searched;
      const dealer = this.selectedDealer();
      if (!dealer) return null;
      return { latitude: dealer.latitude, longitude: dealer.longitude };
    });

    this.dealerLocations = computed(() => {
      const dealers = this.sortedDealers();
      if (!dealers) return [];
      return dealers.map((d) => ({
        latitude: d.latitude,
        longitude: d.longitude
      }));
    });

    const workshopControlChanges = toSignal(
      this.formService.findDealerForm.controls.workshop.valueChanges
    );

    effect(() => {
      const dealerNumber = workshopControlChanges();
      if (dealerNumber) {
        this.autoSortEnabled = false;
        this.sortLocation.set(null);
        this.store.dispatch(
          selectDealer({ dealerNumber, hasSearched: this.hasSearched() })
        );
      }
    });

    effect(() => {
      const dealer = this.selectedDealer();
      if (dealer) {
        const form = this.formService.findDealerForm;
        form.controls.workshop.patchValue(dealer.number, { emitEvent: false });
        form.updateValueAndValidity({ emitEvent: false });

        this.listSortLocation.set({
          latitude: dealer.latitude,
          longitude: dealer.longitude
        });
      }
    });

    this.sortingData = [
      {
        value: "distance",
        view: this.translateService.instant("find-dealer.distance")
      },
      { value: "time", view: this.translateService.instant("find-dealer.time") }
    ];

    effect(() => {
      const dealers = this.sortedDealers();
      const selectedDealer = this.selectedDealer();

      if (dealers && dealers.length === 1 && !selectedDealer) {
        this.formService.findDealerForm.controls.workshop.patchValue(
          dealers[0].number,
          { emitEvent: true }
        );
      }
    });
  }

  onSortChange(value: SortingType): void {
    this.sortingPreference.set(value);
  }

  ngOnInit(): void {
    if (!this.formService.providerIds?.length) {
      this.formService.providerIds =
        this.featureAppService.getFeatureAppDealers();
      this.store.dispatch(
        BookingState.updateProviderIds({
          providerIds: this.formService.providerIds
        })
      );
    }
    if (!this.sortedDealers()) {
      this.store.dispatch(loadDealers({ loadAll: false }));
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.clientLocation.set({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          this.sortingPreference.set("distance");
        },
        (error) => {
          console.error("Geolocation error:", error.message);
          this.sortingPreference.set("time");
        }
      );
    }
  }

  getAllDealers(): void {
    this.store.dispatch(loadDealers({ loadAll: true }));
  }

  onMapLocationSelected(location: ILocation): void {
    const allDealers = this.sortedDealers();
    if (!allDealers) return;

    const clickedDealer = allDealers.find(
      (d) =>
        d.latitude === location.latitude && d.longitude === location.longitude
    );

    this.sortLocation.set(location);
    this.listSortLocation.set(location);

    if (clickedDealer) {
      this.autoSortEnabled = false;
      const form = this.formService.findDealerForm;
      form.markAsTouched();
      form.markAsDirty();
      form.controls.workshop.patchValue(clickedDealer.number, {
        emitEvent: true
      });

      this.sortingPreference.set("distance");
    }
  }

  onLocationSearch(location: ILocation): void {
    this.hasSearched.set(true);
    this.autoSortEnabled = false;
    this.sortLocation.set(location);
    this.listSortLocation.set(location);
  }

  onLocationIdle(location: ILocation): void {
    if (!this.autoSortEnabled) return;
    this.sortLocation.set(location);
    this.listSortLocation.set(location);
  }

  availableTimeLabel(id: string): string {
    const dealers = this.sortedDealers();
    const selection = this.selectedWorkshopSystems();
    if (!dealers || !selection) return "short";

    const workshop = dealers.find((w) => w.number === id);
    if (!workshop) return "short";

    let system = workshop.dealerWorkshopSystemResponses.find((s) =>
      selection.includes(s.workshopSystem)
    );

    if (!system) system = workshop.dealerWorkshopSystemResponses[0];
    if (!system) return "short";

    if (system.workshopSystem == WorkshopSystemEnum.Wheel) return "short-tyre";
    if (system.daysUntilNextFreeTime === null) return "short";
    if (system.daysUntilNextFreeTime > 14) return "long";
    if (system.daysUntilNextFreeTime > 7) return "medium";

    return "short";
  }

  workshopDistance(workshop: DealerResponse): string {
    const location = this.clientLocation();
    if (!location || !workshop) return "";

    const distKm = this.dist(location, {
      longitude: workshop.longitude,
      latitude: workshop.latitude
    });

    if (distKm >= 10) {
      return `${Math.round(distKm / 10)} mil`;
    } else {
      return `${Math.round(distKm)} km`;
    }
  }

  private _sortDealersByDistance(
    dealers: DealerResponse[],
    location: ILocation
  ): DealerResponse[] {
    const sorted = [...dealers];
    sorted.sort(
      (a, b) =>
        this.dist({ latitude: a.latitude, longitude: a.longitude }, location) -
        this.dist({ latitude: b.latitude, longitude: b.longitude }, location)
    );
    return sorted;
  }

  private _sortDealersByTime(
    dealers: DealerResponse[],
    selection: WorkshopSystemEnum[]
  ): DealerResponse[] {
    const sorted = [...dealers];
    const matcher = selection.includes(WorkshopSystemEnum.Service)
      ? WorkshopSystemEnum.Service
      : selection.includes(WorkshopSystemEnum.TechUpdate)
        ? WorkshopSystemEnum.Service
        : selection.includes(WorkshopSystemEnum.Wheel)
          ? WorkshopSystemEnum.Wheel
          : selection.includes(WorkshopSystemEnum.Troubleshooting)
            ? WorkshopSystemEnum.Troubleshooting
            : selection.includes(WorkshopSystemEnum.Windscreen)
              ? WorkshopSystemEnum.Windscreen
              : WorkshopSystemEnum.Service;

    sorted.sort((w1, w2) => {
      const w1System = w1.dealerWorkshopSystemResponses.find(
        (d) => d.workshopSystem === matcher
      );
      const w2System = w2.dealerWorkshopSystemResponses.find(
        (d) => d.workshopSystem === matcher
      );

      if (
        !w1System ||
        !w2System ||
        w1System.firstAvailableTime === null ||
        w2System.firstAvailableTime === null
      ) {
        return 0;
      }

      const time1 = new Date(w1System.firstAvailableTime).getTime();
      const time2 = new Date(w2System.firstAvailableTime).getTime();

      return time1 - time2;
    });
    return sorted;
  }

  private dist(from: ILocation, to: ILocation): number {
    if (!from || !to) return 0;
    const p = 0.017453292519943295;
    const c = Math.cos;
    const a =
      0.5 -
      c((to.latitude - from.latitude) * p) / 2 +
      (c(from.latitude * p) *
        c(to.latitude * p) *
        (1 - c((to.longitude - from.longitude) * p))) /
        2;

    return 12742 * Math.asin(Math.sqrt(a));
  }

  ngOnDestroy(): void {
    this.cleanUp();
  }
}
